/* =============================================================
   SKYFALL — Surveillance interactivity (Shopify port)
   Loader, crosshair, scramble, counters, scrollspy, UTC clock
   ============================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Visibility-gated rAF ----------
     Runs `frame(now)` on each animation frame, but ONLY while at least one of
     `watchEls` is in the viewport AND the tab is visible. A continuously
     re-requesting rAF that paints off-screen (or in a background tab) is dead
     work that keeps the main thread busy and makes the whole page feel choppy.
     Pass no watch element to gate on tab-visibility alone. */
  function gatedRaf(frame, watchEls) {
    var rafId = null;
    var pageVisible = !document.hidden;
    var els = watchEls ? (typeof watchEls.length === 'number' ? Array.prototype.slice.call(watchEls) : [watchEls]) : [];
    var visibleCount = 0;
    var inView = els.length === 0; // no watch target → treat as always in view

    function loop(now) { frame(now); rafId = requestAnimationFrame(loop); }
    function startLoop() {
      if (rafId !== null || !inView || !pageVisible) return;
      rafId = requestAnimationFrame(loop);
    }
    function stopLoop() {
      if (rafId === null) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (els.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { visibleCount += entry.isIntersecting ? 1 : -1; });
        if (visibleCount < 0) visibleCount = 0;
        inView = visibleCount > 0;
        if (inView) startLoop(); else stopLoop();
      }, { threshold: 0 });
      els.forEach(function (el) { io.observe(el); });
    } else {
      inView = true; // no IO support → fall back to visibility-only gating
    }

    document.addEventListener('visibilitychange', function () {
      pageVisible = !document.hidden;
      if (pageVisible) startLoop(); else stopLoop();
    });

    startLoop();
  }

  /* ---------- Loader: counter + reticle morph + logo reveal ---------- */
  // Five reticle designs (SVG paths on 200×200 viewBox) progressing from
  // loose scanning → tight target lock, then the real SIA logo replaces them.
  var LOADER_SHAPES = [
    // 1. Broad crosshair grid — scanning
    { viewBox: '0 0 200 200', paths: [
      { d: 'M100 10 L100 75 M100 125 L100 190 M10 100 L75 100 M125 100 L190 100', fill: 'none', stroke: '#ecedee', strokeWidth: '3' },
      { d: 'M40 40 L60 40 L60 60 L40 60 Z M140 40 L160 40 L160 60 L140 60 Z M40 140 L60 140 L60 160 L40 160 Z M140 140 L160 140 L160 160 L140 160 Z', fill: '#ecedee', stroke: 'none' }
    ]},
    // 2. Circular scope
    { viewBox: '0 0 200 200', paths: [
      { d: 'M100 15 A85 85 0 1 1 99.99 15 Z', fill: 'none', stroke: '#ecedee', strokeWidth: '2.5' },
      { d: 'M100 15 L100 60 M100 140 L100 185 M15 100 L60 100 M140 100 L185 100', fill: 'none', stroke: '#ecedee', strokeWidth: '2.5' },
      { d: 'M100 80 L100 120 M80 100 L120 100', fill: 'none', stroke: '#ecedee', strokeWidth: '2' }
    ]},
    // 3. Four-bracket focus square
    { viewBox: '0 0 200 200', paths: [
      { d: 'M35 80 L35 35 L80 35 M165 80 L165 35 L120 35 M35 120 L35 165 L80 165 M165 120 L165 165 L120 165', fill: 'none', stroke: '#ecedee', strokeWidth: '4' },
      { d: 'M90 100 L110 100 M100 90 L100 110', fill: 'none', stroke: '#ecedee', strokeWidth: '3' },
      { d: 'M100 98 A2 2 0 1 1 99.99 98 Z', fill: '#ecedee', stroke: 'none' }
    ]},
    // 4. Concentric rings — target acquired
    { viewBox: '0 0 200 200', paths: [
      { d: 'M100 30 A70 70 0 1 1 99.99 30 Z', fill: 'none', stroke: '#ecedee', strokeWidth: '2' },
      { d: 'M100 55 A45 45 0 1 1 99.99 55 Z', fill: 'none', stroke: '#ecedee', strokeWidth: '2' },
      { d: 'M100 80 A20 20 0 1 1 99.99 80 Z', fill: 'none', stroke: '#ecedee', strokeWidth: '2' },
      { d: 'M100 96 A4 4 0 1 1 99.99 96 Z', fill: '#ecedee', stroke: 'none' }
    ]},
    // 5. Sniper reticle — locked
    { viewBox: '0 0 200 200', paths: [
      { d: 'M100 20 A80 80 0 1 1 99.99 20 Z', fill: 'none', stroke: '#ecedee', strokeWidth: '3' },
      { d: 'M100 20 L100 50 M100 150 L100 180 M20 100 L50 100 M150 100 L180 100', fill: 'none', stroke: '#ecedee', strokeWidth: '4' },
      { d: 'M43 43 L58 58 M142 43 L157 58 M43 157 L58 142 M142 157 L157 142', fill: 'none', stroke: '#ecedee', strokeWidth: '2' },
      { d: 'M100 85 L100 115 M85 100 L115 100', fill: 'none', stroke: '#ecedee', strokeWidth: '2.5' },
      { d: 'M100 96 A4 4 0 1 1 99.99 96 Z', fill: '#ecedee', stroke: 'none' }
    ]}
  ];

  function renderGeoShape(geoEl, stage) {
    var def = LOADER_SHAPES[stage];
    var paths = '';
    for (var i = 0; i < def.paths.length; i++) {
      var p = def.paths[i];
      paths += '<path d="' + p.d + '" fill="' + p.fill + '" stroke="' + (p.stroke || 'none') + '" stroke-width="' + (p.strokeWidth || 0) + '" stroke-linejoin="miter"/>';
    }
    geoEl.innerHTML = '<svg viewBox="' + def.viewBox + '">' + paths + '</svg>';
  }

  function initLoader() {
    var loader = document.querySelector('[data-sk-loader]');
    if (!loader) return;

    var alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem('sk_surv_loader_shown') === '1'; } catch (e) {}

    if (alreadyShown || reduceMotion) {
      if (loader.parentNode) loader.parentNode.removeChild(loader);
      return;
    }

    var pctEl = loader.querySelector('[data-sk-loader-pct]');
    var barEl = loader.querySelector('[data-sk-loader-line]');
    var shapeEl = loader.querySelector('[data-sk-loader-shape]');
    var geoEl = loader.querySelector('[data-sk-loader-geo]');

    if (!geoEl) return; // safety — markup must include the shape container

    renderGeoShape(geoEl, 0);

    var duration = 2200;         // total count duration
    var reserveForFinal = 300;   // last 300ms is the logo fade-in overlap
    var morphEnd = duration - reserveForFinal;
    var start = performance.now();
    var currentStage = -1;
    var finalized = false;

    function tick(now) {
      var elapsed = now - start;
      var t = Math.min(1, elapsed / duration);
      var eased = 1 - Math.pow(1 - t, 2.4);
      var pct = Math.floor(eased * 100);

      if (pctEl) pctEl.textContent = pct < 10 ? '00' + pct : (pct < 100 ? '0' + pct : '100');
      if (barEl) barEl.style.width = (eased * 100) + '%';

      var stageCount = LOADER_SHAPES.length;
      var morphProgress = Math.min(1, elapsed / morphEnd);
      var stageFloat = Math.min(stageCount - 1, Math.floor(morphProgress * stageCount));
      if (stageFloat !== currentStage) {
        currentStage = stageFloat;
        renderGeoShape(geoEl, stageFloat);
      }

      if (!finalized && elapsed >= morphEnd) {
        finalized = true;
        if (shapeEl) shapeEl.classList.add('is-final');
      }

      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        if (pctEl) pctEl.textContent = '100';
        setTimeout(function () { loader.classList.add('is-wordmark'); }, 400);
        setTimeout(function () { loader.classList.add('is-done'); }, 1400);
        setTimeout(function () {
          try { sessionStorage.setItem('sk_surv_loader_shown', '1'); } catch (e) {}
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        }, 2400);
      }
    }

    requestAnimationFrame(tick);
  }

  /* ---------- Crosshair cursor (desktop only) ---------- */
  function initCrosshair() {
    var root = document.querySelector('[data-sk-crosshair]');
    if (!root) return;
    if (!window.matchMedia('(hover: hover)').matches) {
      root.style.display = 'none';
      return;
    }

    var h = root.querySelector('.sk-crosshair__h');
    var v = root.querySelector('.sk-crosshair__v');
    var core = root.querySelector('.sk-crosshair__core');
    var label = root.querySelector('.sk-crosshair__label');

    // Coalesce pointermove → one DOM write per animation frame. Writing inline
    // styles to 4 elements on every raw pointermove event causes layout/paint
    // thrash whenever the mouse moves; rAF caps it to the display refresh rate.
    var pendingX = 0, pendingY = 0, moveRaf = null;
    function applyMove() {
      moveRaf = null;
      var gx = ((pendingX / window.innerWidth) * 100).toFixed(2);
      var gy = ((pendingY / window.innerHeight) * 100).toFixed(2);
      if (h) h.style.top = pendingY + 'px';
      if (v) v.style.left = pendingX + 'px';
      if (core) {
        core.style.top = pendingY + 'px';
        core.style.left = pendingX + 'px';
      }
      if (label) {
        label.style.top = pendingY + 'px';
        label.style.left = pendingX + 'px';
        label.textContent = 'X ' + gx + ' · Y ' + gy;
      }
    }
    function onMove(e) {
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (moveRaf === null) moveRaf = requestAnimationFrame(applyMove);
    }
    function onLeave() {
      if (moveRaf !== null) { cancelAnimationFrame(moveRaf); moveRaf = null; }
      if (h) h.style.top = '-500px';
      if (v) v.style.left = '-500px';
      if (core) { core.style.top = '-500px'; core.style.left = '-500px'; }
      if (label) { label.style.top = '-500px'; label.style.left = '-500px'; }
    }

    // Park the crosshair offscreen before the first pointermove so it
    // doesn't render stuck at (0,0) in the top-left during page load.
    onLeave();

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave, { passive: true });
  }

  /* ---------- Scramble text ---------- */
  var SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/-*!<>?#';

  function scrambleEl(el, speed) {
    if (reduceMotion) return;
    speed = speed || 40;
    var text = el.getAttribute('data-sk-scramble') || el.textContent;
    el.textContent = text;
    var frame = 0;
    var totalFrames = Math.ceil(text.length * 1.3);
    var timer;

    function tick() {
      var revealed = Math.floor((frame / totalFrames) * text.length);
      var out = '';
      for (var i = 0; i < text.length; i++) {
        var ch = text.charAt(i);
        if (ch === ' ' || ch === '\n') { out += ch; continue; }
        if (i < revealed) { out += ch; continue; }
        out += SCRAMBLE_CHARS.charAt(Math.floor(Math.random() * SCRAMBLE_CHARS.length));
      }
      el.textContent = out;
      frame++;
      if (frame <= totalFrames) {
        timer = setTimeout(tick, speed);
      } else {
        el.textContent = text;
      }
    }
    tick();
  }

  function initScrambles() {
    var els = document.querySelectorAll('[data-sk-scramble]');
    els.forEach(function (el) {
      // delay past loader
      setTimeout(function () { scrambleEl(el); }, 400);
    });
  }

  /* ---------- Counter (intersection-triggered) ---------- */
  function initCounters() {
    var counters = document.querySelectorAll('[data-sk-counter]');
    if (!counters.length || !('IntersectionObserver' in window)) {
      counters.forEach(function (el) {
        el.textContent = (el.dataset.skPrefix || '') + el.dataset.skCounter + (el.dataset.skSuffix || '');
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var to = parseFloat(el.dataset.skCounter) || 0;
        var duration = parseInt(el.dataset.skDuration || '1800', 10);
        var decimals = parseInt(el.dataset.skDecimals || '0', 10);
        var prefix = el.dataset.skPrefix || '';
        var suffix = el.dataset.skSuffix || '';
        var start = performance.now();

        function step(now) {
          var t = Math.min(1, (now - start) / duration);
          var eased = 1 - Math.pow(1 - t, 3);
          var v = (to * eased).toFixed(decimals);
          el.textContent = prefix + v + suffix;
          if (t < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.3 });

    counters.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Eastern clock (America/Toronto, DST-aware) ---------- */
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function fmtUtc(d) { return d.toLocaleTimeString('en-GB', { timeZone: 'America/Toronto', hour12: false }); }

  function initClocks() {
    var clocks = document.querySelectorAll('[data-sk-utc-clock]');
    if (!clocks.length) return;
    function tick() {
      var t = fmtUtc(new Date());
      clocks.forEach(function (el) { el.textContent = t; });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ---------- Drifting coordinates (hero panel + footer) ---------- */
  function initDriftingCoords() {
    var coords = document.querySelectorAll('[data-sk-coord]');
    if (!coords.length || reduceMotion) return;

    var start = performance.now();
    gatedRaf(function (t) {
      var dt = (t - start) / 1000;
      coords.forEach(function (el) {
        var base = parseFloat(el.dataset.skCoord) || 0;
        var decimals = parseInt(el.dataset.skDecimals || '4', 10);
        var suffix = el.dataset.skSuffix || '';
        var drifted = base + Math.sin(dt * 0.7) * 0.00018 + Math.sin(dt * 0.31) * 0.00011;
        el.textContent = drifted.toFixed(decimals) + suffix;
      });
    }, coords);
  }

  /* ---------- Radar sweep ---------- */
  function initRadar() {
    var radar = document.querySelector('[data-sk-radar-sweep]');
    if (!radar || reduceMotion) return;
    var start = performance.now();
    gatedRaf(function (t) {
      var sweep = ((t - start) / 5000) % 1;
      radar.setAttribute('transform', 'rotate(' + (sweep * 360) + ' 50 50)');
    }, radar);
  }

  /* ---------- Scrollspy (topnav) ---------- */
  function initScrollspy() {
    var links = document.querySelectorAll('[data-sk-spy-link]');
    if (!links.length || !('IntersectionObserver' in window)) return;

    var ids = [];
    links.forEach(function (link) {
      var href = link.getAttribute('href');
      if (href && href.charAt(0) === '#') ids.push(href.substring(1));
    });

    var targets = ids.map(function (id) { return document.getElementById(id); }).filter(Boolean);
    if (!targets.length) return;

    var activeId = ids[0];
    function paint() {
      links.forEach(function (link) {
        var href = link.getAttribute('href');
        if (!href) return;
        if (href === '#' + activeId) link.classList.add('is-active');
        else link.classList.remove('is-active');
      });
    }

    var io = new IntersectionObserver(function (entries) {
      var visible = entries
        .filter(function (e) { return e.isIntersecting; })
        .sort(function (a, b) { return a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top; });
      if (visible[0]) {
        activeId = visible[0].target.id;
        paint();
      }
    }, { rootMargin: '-20% 0px -60% 0px', threshold: 0 });

    targets.forEach(function (t) { io.observe(t); });
    paint();
  }

  /* ---------- Mobile menu overlay ---------- */
  function initMobileMenu() {
    var btn = document.querySelector('[data-sk-menu-toggle]');
    var overlay = document.querySelector('[data-sk-menu-overlay]');
    if (!btn || !overlay) return;

    function open() {
      document.body.classList.add('sk-surv-menu-open');
      btn.setAttribute('aria-expanded', 'true');
    }
    function close() {
      document.body.classList.remove('sk-surv-menu-open');
      btn.setAttribute('aria-expanded', 'false');
    }

    btn.addEventListener('click', function () {
      if (document.body.classList.contains('sk-surv-menu-open')) close();
      else open();
    });

    overlay.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', close);
    });

    var closeBtn = overlay.querySelector('[data-sk-menu-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* ---------- Hero parallax (scroll-linked, hero only — bg stays static) ---------- */
  function initParallax() {
    if (reduceMotion) return;
    var hero = document.querySelector('.sk-hero');
    if (!hero) return;
    var ticking = false;
    function update() {
      ticking = false;
      var rect = hero.getBoundingClientRect();
      var progress = Math.max(0, Math.min(1, -rect.top / rect.height));
      hero.style.transform = 'translate3d(0,' + (-progress * 40) + 'px, 0)';
      hero.style.opacity = String(1 - progress * 0.4);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      requestAnimationFrame(update);
      ticking = true;
    }, { passive: true });
    update();
  }

  /* ---------- Text split: wrap each word in a span for stagger reveal ---------- */
  function initWordSplit() {
    var targets = document.querySelectorAll('[data-sk-split-words]');
    targets.forEach(function (el) {
      if (el.dataset.skSplitDone === '1') return;
      var text = el.textContent.trim();
      if (!text) return;
      var words = text.split(/\s+/);
      var step = parseInt(el.dataset.skWordStep || '70', 10);
      el.innerHTML = '';
      words.forEach(function (w, i) {
        var wrap = document.createElement('span');
        wrap.className = 'sk-word-wrap';
        var inner = document.createElement('span');
        inner.className = 'sk-word';
        inner.style.setProperty('--sk-word-delay', (i * step) + 'ms');
        inner.textContent = w;
        wrap.appendChild(inner);
        el.appendChild(wrap);
      });
      el.dataset.skSplitDone = '1';
    });
  }

  /* ---------- Text split: wrap each [data-sk-line] content in .sk-line-inner ---------- */
  function initLineWrap() {
    var targets = document.querySelectorAll('[data-sk-line]');
    targets.forEach(function (el) {
      if (el.dataset.skLineDone === '1') return;
      // If it already has the inner wrap (e.g., manually written), skip
      if (el.querySelector(':scope > .sk-line-inner')) { el.dataset.skLineDone = '1'; return; }
      var inner = document.createElement('span');
      inner.className = 'sk-line-inner';
      while (el.firstChild) inner.appendChild(el.firstChild);
      el.appendChild(inner);
      el.dataset.skLineDone = '1';
    });
  }

  /* ---------- Scroll-reveal (IntersectionObserver) ---------- */
  function initScrollReveal() {
    initWordSplit();
    initLineWrap();

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-sk-reveal], [data-sk-reveal-stagger], [data-sk-line], [data-sk-split-words], [data-sk-clip-reveal]')
        .forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var singles = document.querySelectorAll('[data-sk-reveal], [data-sk-line], [data-sk-split-words], [data-sk-clip-reveal]');
    var staggers = document.querySelectorAll('[data-sk-reveal-stagger]');
    if (!singles.length && !staggers.length) return;

    staggers.forEach(function (container) {
      var step = parseInt(container.dataset.skStaggerStep || '70', 10);
      var maxDelay = parseInt(container.dataset.skStaggerMax || '500', 10);
      Array.prototype.forEach.call(container.children, function (child, i) {
        child.style.setProperty('--i-delay', Math.min(i * step, maxDelay) + 'ms');
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    singles.forEach(function (el) { io.observe(el); });
    staggers.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Boot ---------- */
  function boot() {
    // Mark body so CSS can target
    document.body.classList.add('sk-surv');

    initLoader();
    initCrosshair();
    initScrambles();
    initCounters();
    initClocks();
    initDriftingCoords();
    initRadar();
    initScrollspy();
    initMobileMenu();
    initScrollReveal();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor hot-reload
  document.addEventListener('shopify:section:load', function () {
    initScrambles();
    initCounters();
    initClocks();
    initDriftingCoords();
    initRadar();
    initScrollspy();
    initMobileMenu();
  });
})();
